<style lang="scss">
  @use "../../../../../assets/css/global.scss" as *;
  @use "../../../../../assets/css/_colors.scss" as *;
  #dashboardSearchInput {
    display: flex;
    gap: 1rem;
    position: relative;

    #autocompleteDropdown {
      width: 30rem;
      max-height: 40vh;
      background-color: white;
      border-radius: 0.625rem;
      box-shadow: $subtle-shadow;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      gap: 1.5rem;
      padding: 1.5rem 1rem;
      position: absolute;
      right: 0;
      top: calc(100% + 0.5rem);
      white-space: nowrap;
      z-index: 99;

      #dashboardSearchNav {
        display: flex;
        justify-content: space-around;

        .resultCategory {
          border-radius: 0.625rem;
          color: $text_black;
          cursor: pointer;
          font-size: 85%;
          font-weight: bold;
          text-transform: uppercase;
          padding: 0.5rem;

          &.active,
          &:hover {
            background-color: $light-gray;
          }

          &.active {
            color: $blue;
            cursor: default;
          }
        }
      }

      #searchResults {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        #noResults {
          margin: 1rem 0;
          text-align: center;
        }

        .resultType {
          min-width: 100%;
          width: fit-content;
          align-items: center;
          border-radius: 0.625rem;
          display: flex;
          gap: 0.5rem;
          padding: 0.5rem;
          user-select: none;

          &:hover {
            background-color: $light-gray;
          }
        }
      }
    }
  }
</style>

<script>
  import { derived, writable } from "svelte/store";
  import {
    dashboardCampaignsStore,
    dashboardCandidatesQueryStore,
    dashboardProjectsStore,
  } from "../../../../api-stores/dashboard-store";
  import { dashboardSearchTerm } from "./stores";

  import SearchInput from "../../../components/legacy/SearchInput.svelte";

  const formattedCandidates = derived(
    dashboardCandidatesQueryStore,
    ($dashboardCandidatesQueryStore) =>
      $dashboardCandidatesQueryStore.data.map((candidate) => ({
        ...candidate,
        value: candidate.name,
        id: candidate.candidateId,
        type: "contact",
      }))
  );

  const formattedProjects = derived(
    [dashboardProjectsStore, dashboardSearchTerm],
    ([$dashboardProjectsStore, $dashboardSearchTerm]) =>
      $dashboardProjectsStore
        .filter((p) =>
          p.projectName
            .toLowerCase()
            .includes($dashboardSearchTerm.toLowerCase())
        )
        .map((p) => ({
          value: p.projectName,
          id: p.projectId,
          type: "project",
        }))
  );

  const formattedCampaigns = derived(
    [dashboardCampaignsStore, dashboardSearchTerm],
    ([$dashboardCampaignsStore, $dashboardSearchTerm]) =>
      $dashboardCampaignsStore
        .filter((c) =>
          c.campaignName
            .toLowerCase()
            .includes($dashboardSearchTerm.toLowerCase())
        )
        .map((c) => ({
          value: c.campaignName,
          id: c.campaignId,
          type: "campaign",
        }))
  );

  const allResults = derived(
    [formattedCandidates, formattedProjects, formattedCampaigns],
    ([$formattedCandidates, $formattedProjects, $formattedCampaigns]) => [
      ...$formattedCandidates,
      ...$formattedProjects,
      ...$formattedCampaigns,
    ]
  );

  const activeCategory = writable("all");

  const filteredResults = derived(
    [allResults, activeCategory],
    ([$allResults, $activeCategory]) =>
      $allResults.filter((result) =>
        ["all", result.type].includes($activeCategory)
      )
  );

  let showDropdown = false;
  let showResults = false;

  const resultIconMap = {
    campaign: "fa-bullhorn",
    project: "fa-notebook",
    contact: "fa-user",
  };

  const categoryTitleMap = {
    all: "All",
    contact: "Contacts",
    project: "Projects",
    campaign: "Campaigns",
  };

  const handleSearch = async () => {
    if ($dashboardSearchTerm.length < 2) {
      showDropdown = false;
      showResults = false;
      return;
    }

    showDropdown = true;

    dashboardCampaignsStore.once().then(() => {
      showResults = true;
    });
    dashboardProjectsStore.once().then(() => {
      showResults = true;
    });
    dashboardCandidatesQueryStore.get().then(() => {
      showResults = true;
    });
  };

  let timerId;
  const handleInputEvent = () => {
    clearTimeout(timerId);
    timerId = setTimeout(() => handleSearch(), 300);
  };

  const handleCategoryClick = (category) => {
    activeCategory.set(category);
  };

  const handleKeyPress = (event, category) => {
    if (event.key === "Enter") {
      handleCategoryClick(category);
    }
  };

  const resetSearchInput = () => {
    activeCategory.set("all");
    showDropdown = false;
    showResults = false;
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest("#dashboardSearchInput")) {
      resetSearchInput();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      resetSearchInput();
    }
  };

  const getResultLink = (result) => {
    const baseLink = result.type === "contact" ? "contacts" : result.type;
    const queryParam =
      result.type === "contact" ? "contact" : `${result.type}Id`;
    // const queryValue = result.type === "contact" ? result.value : result.id;
    const queryValue = result.type === "contact" ? result.name : result.id;
    return `/${baseLink}?${queryParam}=${queryValue}`;
  };
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleKeyDown} />

<div id="dashboardSearchInput">
  <SearchInput
    bind:value={$dashboardSearchTerm}
    searchIcon={!$dashboardSearchTerm.length}
    showDelete={$dashboardSearchTerm.length}
    style="width:14rem"
    onInput={handleInputEvent}
    onDelete={() => dashboardSearchTerm.set("")}
    preventDefaultEsc
  />
  {#if showDropdown}
    <div id="autocompleteDropdown">
      <div id="dashboardSearchNav">
        {#each ["all", "contact", "project", "campaign"] as category}
          <div
            role="button"
            tabindex="0"
            class="resultCategory"
            class:active={$activeCategory === category}
            on:click={() => handleCategoryClick(category)}
            on:keypress={(event) => handleKeyPress(event, category)}
          >
            {categoryTitleMap[category]}
          </div>
        {/each}
      </div>
      <div id="searchResults">
        {#if showResults}
          {#if !$filteredResults.length}
            <div id="noResults">No search results found</div>
          {:else}
            {#each $filteredResults as result}
              <a
                class="resultType resultType-{result.type}"
                href={getResultLink(result)}
                target="_blank"
              >
                <i class="fa-regular {resultIconMap[result.type]}"></i>
                {result.value}
                {#if result.emails}
                  <span class="emailResults">{result.emails.join(", ")}</span>
                {/if}
              </a>
            {/each}
          {/if}
        {/if}
      </div>
    </div>
  {/if}
</div>
